<template>
  <div class="dashboard-bi">
    <TopBarTitleComponent title="Dashboard" :showBackIcon="false">
      <template slot="right-area">
        <div class="header-right-actions">
          <button
            type="button"
            :class="['privacy-toggle', { active: hideValues }]"
            :title="hideValues ? 'Mostrar valores' : 'Ocultar valores'"
            @click="togglePrivacy"
          >
            <svg
              v-if="!hideValues"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="12"
                cy="12"
                r="3"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <line
                x1="1"
                y1="1"
                x2="23"
                y2="23"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <PeriodFilter v-model="period" @change="onPeriodChange" />
        </div>
      </template>
    </TopBarTitleComponent>

    <!-- ── Skeleton mientras el plan no ha cargado ── -->
    <b-container
      v-if="!planReady"
      fluid
      class="dashboard-bi-content px-lg-4 pt-3 pb-4"
    >
      <b-row class="mb-3">
        <b-col xs="12" xl="5" class="mb-3 mb-xl-0">
          <div class="bi-skel bi-skel--banner"></div>
        </b-col>
        <b-col xs="12" xl="7">
          <div class="bi-skel-kpis">
            <div
              class="bi-skel bi-skel--kpi"
              v-for="i in 3"
              :key="'k' + i"
            ></div>
          </div>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <div class="bi-skel bi-skel--secondary"></div>
        </b-col>
        <b-col xs="12" xl="6">
          <div class="bi-skel bi-skel--secondary"></div>
        </b-col>
      </b-row>
      <b-row>
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <div class="bi-skel bi-skel--chart"></div>
        </b-col>
        <b-col xs="12" xl="6">
          <div class="bi-skel bi-skel--chart"></div>
        </b-col>
      </b-row>
    </b-container>

    <!-- ── Vista base / Lanza (sin bi_dashboard_full) ── -->
    <b-container
      v-else-if="!hasBIFull"
      fluid
      class="dashboard-bi-content px-lg-4 pt-3 pb-4"
    >
      <b-row class="mb-3">
        <b-col xs="12" xl="5" class="mb-3 mb-xl-0">
          <PlansCardComponent />
        </b-col>
        <b-col xs="12" xl="7">
          <DashboardKpis
            :period="period"
            :hideCollection="true"
            @open-overdue-invoices="
              $root.$emit('notification-action', 'overdue-invoices')
            "
            @open-pending-collection="
              $root.$emit('notification-action', 'pending-collection')
            "
            @collection-data="onCollectionData"
          />
        </b-col>
      </b-row>
      <!-- Fila cobros: solo si tracking está habilitado -->
      <b-row v-if="collectionData.trackingEnabled" class="mb-3">
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <KpiSecondaryCard
            label="Cobros pendientes"
            :value="collectionData.collectionRate.pending_amount"
            prefix="$"
            icon="el-icon-money"
            iconColor="#534ab7"
            iconBg="#ece8ff"
            tooltip="Monto total pendiente de cobro"
            :subtextLine="collectionData.collectionSubtext"
            :clickable="collectionData.collectionRate.pending_amount > 0"
            @click="$root.$emit('notification-action', 'pending-collection')"
          />
        </b-col>
        <b-col xs="12" xl="6">
          <KpiSecondaryCard
            label="Facturas vencidas"
            :value="collectionData.overdueInvoices.value"
            prefix="$"
            icon="el-icon-warning-outline"
            iconColor="#e24b4a"
            iconBg="#fff0f0"
            :danger="collectionData.overdueInvoices.count > 0"
            tooltip="Facturas pendientes de pago que superaron su fecha de vencimiento"
            :subtextLine="collectionData.overdueSubtext"
            :clickable="
              collectionData.overdueInvoices.count > 0 ||
                collectionData.overdueInvoices.value > 0
            "
            @click="$root.$emit('notification-action', 'overdue-invoices')"
          />
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Alertas</h6>
              <span v-if="alertCount > 0" class="bi-card-count">{{
                alertCount
              }}</span>
            </div>
            <SmartAlerts
              @manage-expired-quotes="openExpiredQuotes"
              @view-dormant-clients="openDormantClients"
            />
          </div>
        </b-col>
        <b-col xs="12" xl="6">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Cotizaciones recientes</h6>
            </div>
            <RecentQuotes :period="period" @open-quote="openCRMWithQuote" />
          </div>
        </b-col>
      </b-row>
      <!-- Upgrade card para BI completo -->
      <b-row class="mb-3">
        <b-col xs="12">
          <div class="bi-upgrade-card bi-upgrade-card--inline">
            <div class="bi-upgrade-card__icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#3ecfb4"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="20" x2="18" y2="10"></line>
                <line x1="12" y1="20" x2="12" y2="4"></line>
                <line x1="6" y1="20" x2="6" y2="14"></line>
              </svg>
            </div>
            <div class="bi-upgrade-card__text">
              <span class="bi-upgrade-card__title"
                >Embudo comercial y top clientes</span
              >
              <span class="bi-upgrade-card__desc"
                >Análisis avanzado disponible en el plan Crece o superior.</span
              >
            </div>
            <router-link :to="{ name: 'plans' }" class="bi-upgrade-card__btn"
              >Ver planes</router-link
            >
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- ── Vista BI completa (plan Crece+) ── -->
    <b-container v-else fluid class="dashboard-bi-content px-lg-4 pt-3 pb-4">
      <!-- Fila 1: Banner bienvenida + KPIs (6 metricas) -->
      <b-row class="mb-3">
        <b-col xs="12" xl="5" class="mb-3 mb-xl-0">
          <PlansCardComponent />
        </b-col>
        <b-col xs="12" xl="7">
          <DashboardKpis
            :period="period"
            :hideCollection="true"
            @open-overdue-invoices="
              $root.$emit('notification-action', 'overdue-invoices')
            "
            @open-pending-collection="
              $root.$emit('notification-action', 'pending-collection')
            "
            @collection-data="onCollectionData"
          />
        </b-col>
      </b-row>

      <!-- Fila 2: Cobros pendientes + Facturas vencidas (ancho completo) -->
      <b-row v-if="collectionData.trackingEnabled" class="mb-3">
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <KpiSecondaryCard
            label="Cobros pendientes"
            :value="collectionData.collectionRate.pending_amount"
            prefix="$"
            icon="el-icon-money"
            iconColor="#534ab7"
            iconBg="#ece8ff"
            tooltip="Monto total pendiente de cobro"
            :subtextLine="collectionData.collectionSubtext"
            :clickable="collectionData.collectionRate.pending_amount > 0"
            @click="$root.$emit('notification-action', 'pending-collection')"
          />
        </b-col>
        <b-col xs="12" xl="6">
          <KpiSecondaryCard
            label="Facturas vencidas"
            :value="collectionData.overdueInvoices.value"
            prefix="$"
            icon="el-icon-warning-outline"
            iconColor="#e24b4a"
            iconBg="#fff0f0"
            :danger="collectionData.overdueInvoices.count > 0"
            tooltip="Facturas pendientes de pago que superaron su fecha de vencimiento"
            :subtextLine="collectionData.overdueSubtext"
            :clickable="
              collectionData.overdueInvoices.count > 0 ||
                collectionData.overdueInvoices.value > 0
            "
            @click="$root.$emit('notification-action', 'overdue-invoices')"
          />
        </b-col>
      </b-row>

      <!-- Fila 3: Embudo + Alertas -->
      <b-row class="mb-3">
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Embudo comercial</h6>
              <el-tooltip
                content="Visualiza las etapas de tu proceso de ventas y la conversión entre cada una"
                placement="top"
                effect="dark"
              >
                <i class="el-icon-info bi-card-info"></i>
              </el-tooltip>
            </div>
            <SalesFunnel :period="period" />
          </div>
        </b-col>
        <b-col xs="12" xl="6">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Alertas</h6>
              <span v-if="alertCount > 0" class="bi-card-count">{{
                alertCount
              }}</span>
            </div>
            <SmartAlerts
              @manage-expired-quotes="openExpiredQuotes"
              @view-dormant-clients="openDormantClients"
            />
          </div>
        </b-col>
      </b-row>

      <!-- Fila 3: Top Clientes + Cotizaciones Recientes -->
      <b-row>
        <b-col xs="12" xl="6" class="mb-3 mb-xl-0">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Top clientes</h6>
              <el-tooltip
                content="Clientes con mayor facturación en el período seleccionado"
                placement="top"
                effect="dark"
              >
                <i class="el-icon-info bi-card-info"></i>
              </el-tooltip>
            </div>
            <TopClients :period="period" @open-client="openCRMDrawer" />
          </div>
        </b-col>
        <b-col xs="12" xl="6">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Cotizaciones recientes</h6>
              <el-tooltip
                content="Últimas cotizaciones creadas con su estado actual"
                placement="top"
                effect="dark"
              >
                <i class="el-icon-info bi-card-info"></i>
              </el-tooltip>
            </div>
            <RecentQuotes :period="period" @open-quote="openCRMWithQuote" />
          </div>
        </b-col>
      </b-row>

      <!-- Fila 4: Documentos pendientes de firma -->
      <b-row
        class="mt-3"
        v-if="
          $store.getters.hasFeature('signing') &&
            docSummary &&
            docSummary.counts &&
            docSummary.counts.total > 0
        "
      >
        <b-col cols="12">
          <div class="bi-card">
            <div class="bi-card-header">
              <h6 class="bi-card-title">Firma electrónica</h6>
              <router-link :to="{ name: 'documentos' }" class="bi-card-link"
                >Ver todos</router-link
              >
            </div>
            <div class="doc-summary-strip">
              <div
                class="doc-summary-strip__item"
                @click="$router.push({ name: 'documentos' })"
              >
                <span class="doc-summary-strip__count">{{
                  docSummary.counts.sent || 0
                }}</span>
                <span class="doc-summary-strip__label">Pendientes firma</span>
              </div>
              <div
                class="doc-summary-strip__item"
                @click="$router.push({ name: 'documentos' })"
              >
                <span
                  class="doc-summary-strip__count doc-summary-strip__count--green"
                  >{{ docSummary.counts.signed || 0 }}</span
                >
                <span class="doc-summary-strip__label">Completados</span>
              </div>
              <div
                class="doc-summary-strip__item"
                v-if="docSummary.expiring_soon > 0"
              >
                <span
                  class="doc-summary-strip__count doc-summary-strip__count--red"
                  >{{ docSummary.expiring_soon }}</span
                >
                <span class="doc-summary-strip__label">Por expirar</span>
              </div>
              <div
                class="doc-summary-strip__item"
                @click="$router.push({ name: 'documentos.sign-flow' })"
              >
                <button class="doc-summary-strip__btn">
                  <i class="el-icon-edit-outline"></i> Firmar PDF
                </button>
              </div>
            </div>
            <!-- Pending docs list -->
            <div
              v-if="
                docSummary.pending_documents &&
                  docSummary.pending_documents.length
              "
              class="doc-pending-list"
            >
              <div
                v-for="doc in docSummary.pending_documents"
                :key="doc.id"
                class="doc-pending-item"
                @click="
                  $router.push({
                    name: 'documentos.detail',
                    params: { id: doc.id }
                  })
                "
              >
                <i class="el-icon-document"></i>
                <span class="doc-pending-item__name">{{ doc.name }}</span>
                <span class="doc-pending-item__status">{{
                  docStatusLabel(doc.status)
                }}</span>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- Barra flotante del copiloto -->
    <CopilotFloatingBar />

    <!-- ── Modal: Noticia / Anuncio ───────────────────────────────────────── -->
    <el-dialog
      v-if="news"
      :visible.sync="showNews"
      append-to-body
      :show-close="false"
      :custom-class="
        newsType === 'html' ? 'news-modal news-modal--html' : 'news-modal'
      "
    >
      <!-- Header: solo para imagen/video -->
      <div v-if="newsType !== 'html'" slot="title" class="news-modal__header">
        <span class="news-modal__title">{{ news.title }}</span>
        <button
          class="news-modal__close"
          @click="closeNews('close')"
          aria-label="Cerrar"
        >
          <i class="el-icon-close"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="news-modal__body">
        <!-- Imagen -->
        <div
          v-if="newsType === 'image' && news.imageUrl"
          class="news-modal__media"
        >
          <img
            :src="news.imageUrl"
            :alt="news.title"
            class="news-modal__image"
          />
        </div>
        <!-- Video embed -->
        <div
          v-if="newsType === 'video' && news.link"
          class="news-modal__video-wrapper"
        >
          <iframe
            :src="news.link"
            frameborder="0"
            allowfullscreen
            class="news-modal__video"
          ></iframe>
        </div>
        <!-- HTML: iframe con body transparente (CSS inyectado vía patchedHtml) -->
        <iframe
          v-if="newsType === 'html' && news.html_content"
          ref="newsIframe"
          :srcdoc="patchedHtml"
          :style="{ height: (htmlIframeHeight || 400) + 'px' }"
          class="news-modal__iframe"
          frameborder="0"
          allowtransparency="true"
          sandbox="allow-same-origin allow-scripts"
          scrolling="no"
          @load="_resizeIframe"
        ></iframe>
        <!-- Texto descripción (imagen y video) -->
        <div
          v-if="newsType !== 'html' && news.content"
          class="news-modal__text"
        >
          <p>{{ news.content }}</p>
        </div>
      </div>

      <!-- Footer: solo para imagen/video -->
      <div v-if="newsType !== 'html'" slot="footer" class="news-modal__footer">
        <button class="news-modal__btn-close" @click="closeNews('close')">
          Cerrar
        </button>
        <a
          v-if="news.cta_url && news.cta_text"
          :href="news.cta_url"
          :target="news.cta_target || '_self'"
          :rel="
            news.cta_target === '_blank' ? 'noopener noreferrer' : undefined
          "
          class="news-modal__btn-cta"
          @click="closeNews('cta')"
          >{{ news.cta_text }}</a
        >
      </div>

      <!-- Botón X flotante para variante HTML (superpuesto sobre el iframe) -->
      <button
        v-if="newsType === 'html'"
        class="news-modal__close-html"
        @click="closeNews('close')"
        aria-label="Cerrar"
      >
        <i class="el-icon-close"></i>
      </button>
    </el-dialog>

    <!-- Modal: Mensaje masivo -->
    <BulkMessageModal
      :visible.sync="showBulkMessageModal"
      :recipients="bulkMessageRecipients"
      :context="bulkMessageContext"
      @sent="onMessageSent"
    />

    <!-- Drawer: CRM de cliente -->
    <ClientCRMDrawer
      :visible.sync="showCRMDrawer"
      :client-data="crmDrawerClient"
      @status-changed="onClientStatusChanged"
      @send-message="onCRMSendMessage"
    />
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import PlansCardComponent from "../../plans/components/PlansCard";
import PeriodFilter from "../components/PeriodFilter";
import DashboardKpis from "../components/DashboardKpis";
import SalesFunnel from "../components/SalesFunnel";
import SmartAlerts from "../components/SmartAlerts";
import TopClients from "../components/TopClients";
import RecentQuotes from "../components/RecentQuotes";
import BulkMessageModal from "../components/BulkMessageModal";
import ClientCRMDrawer from "../components/ClientCRMDrawer";
import KpiSecondaryCard from "../components/KpiSecondaryCard";
import documentoService from "../../documentos/services/documentoService";
import dashboardService from "../services/dashboardService";
import homeDashboardService from "../../home/services/dashboardService";
import CopilotFloatingBar from "@/components/Copilot/CopilotFloatingBar";

export default {
  name: "DashboardBIPage",
  computed: {
    newsType() {
      if (!this.news) return "image";
      if (this.news.type === "html" || !!this.news.html_content) return "html";
      return this.news.type || "image";
    },

    /**
     * Devuelve el HTML del template con el fondo del body eliminado y
     * un override de transparencia inyectado al final del <head>.
     * Esto evita que el background oscuro del template (típico en emails)
     * se muestre dentro del iframe del modal.
     */
    patchedHtml() {
      if (!this.news || !this.news.html_content) return "";
      let out = this.news.html_content;

      // Eliminar background / background-color dentro de la regla body { … }
      out = out.replace(
        /(body\s*\{[^}]*?)background(?:-color)?\s*:[^;}\n]+;?/gi,
        "$1"
      );

      // Override con !important al final del <head> — gana siempre
      const override =
        "<style>" +
        "html,body{" +
        "background:transparent!important;" +
        "background-color:transparent!important;" +
        "min-height:0!important;" +
        "height:auto!important;" +
        "display:block!important;" +
        "align-items:unset!important;" +
        "justify-content:unset!important;" +
        "padding:0!important;" +
        "margin:0!important;" +
        "overflow-x:hidden!important;" +
        "overflow-y:visible!important;" +
        "}" +
        "</style>";

      out = out.includes("</head>")
        ? out.replace("</head>", override + "</head>")
        : override + out;

      return out;
    },
    planReady() {
      return this.$store.getters.userPlanLoaded;
    },
    hasBIFull() {
      return this.$store.getters.hasFeature("bi_dashboard_full");
    },
    alertCount() {
      return this.$store.state.notificationsStore
        ? this.$store.state.notificationsStore.unreadCount || 0
        : 0;
    }
  },
  components: {
    TopBarTitleComponent,
    PlansCardComponent,
    PeriodFilter,
    DashboardKpis,
    SalesFunnel,
    SmartAlerts,
    TopClients,
    RecentQuotes,
    BulkMessageModal,
    ClientCRMDrawer,
    KpiSecondaryCard,
    CopilotFloatingBar
  },
  data() {
    return {
      period: "month",
      hideValues: localStorage.getItem("kpi-hide-values") === "true",
      // Bulk Message Modal
      showBulkMessageModal: false,
      bulkMessageRecipients: [],
      bulkMessageContext: "quotes",
      // CRM Drawer
      showCRMDrawer: false,
      crmDrawerClient: null,
      // News modal
      showNews: false,
      news: null,
      htmlIframeHeight: 0,
      // Document summary
      docSummary: null,
      // Collection KPIs (received from DashboardKpis)
      collectionData: {
        collectionRate: {
          value: 0,
          pending_amount: 0,
          paid_count: 0,
          sent_count: 0
        },
        overdueInvoices: { value: 0, count: 0, avg_days: 0 },
        collectionSubtext: "",
        overdueSubtext: "",
        trackingEnabled: false
      }
    };
  },
  methods: {
    async getNews() {
      try {
        const resp = await homeDashboardService.getNews();
        const payload = resp && resp.data ? resp.data : null;
        if (payload && payload.success && payload.data) {
          this.news = payload.data;
          this.showNews = true;
        }
      } catch {
        // non-critical — silently ignore
      }
    },

    async closeNews(action) {
      this.showNews = false;
      if (!this.news) return;
      try {
        await homeDashboardService.postNewsNoShow({
          news_id: this.news.id,
          action: action || "close"
        });
      } catch {
        // analytics failure does not affect UX
      }
    },

    onPeriodChange(val) {
      // Warm the summary cache BEFORE setting period so child watchers share the in-flight request
      dashboardService.prefetchSummary(val);
      this.period = val;
    },
    togglePrivacy() {
      this.hideValues = !this.hideValues;
      localStorage.setItem("kpi-hide-values", this.hideValues);
      this.$root.$emit("privacy-changed", this.hideValues);
    },
    onCollectionData(data) {
      this.collectionData = data;
    },
    // Expired quotes — delegated to DashboardLayout
    openExpiredQuotes() {
      this.$root.$emit("notification-action", "expired-quotes");
    },

    // Dormant clients — delegated to DashboardLayout
    openDormantClients() {
      this.$root.$emit("notification-action", "dormant-clients");
    },

    // CRM Drawer
    openCRMDrawer(client) {
      this.crmDrawerClient = client;
      this.showCRMDrawer = true;
    },

    // From RecentQuotes @open-quote
    openCRMWithQuote(quote) {
      this.crmDrawerClient = {
        id: quote.client_id || null,
        name: quote.client_name || "Cliente",
        status: null,
        conversion_score: null,
        days_since_last_invoice: quote.days_ago || null,
        total_quotes: null,
        converted_quotes: null,
        _fromQuote: quote
      };
      this.showCRMDrawer = true;
    },

    // Bulk message from ExpiredQuotesModal
    onBulkMessage({ recipients, context }) {
      this.bulkMessageRecipients = recipients;
      this.bulkMessageContext = context || "quotes";
      this.showBulkMessageModal = true;
    },

    // CRM send message
    onCRMSendMessage({ recipients, context }) {
      this.bulkMessageRecipients = recipients;
      this.bulkMessageContext = context || "clients";
      this.showBulkMessageModal = true;
    },

    onMessageSent() {
      // callback after message sent
    },

    onClientStatusChanged({ clientId, newStatus }) {
      if (this.crmDrawerClient && this.crmDrawerClient.id === clientId) {
        this.crmDrawerClient = { ...this.crmDrawerClient, status: newStatus };
      }
    },

    // Document summary
    async loadDocSummary() {
      try {
        const { data } = await documentoService.getDocumentsSummary();
        this.docSummary = data.data || data;
      } catch (e) {
        // silently fail — widget simply won't show
        this.docSummary = null;
      }
    },

    _resizeIframe() {
      const measure = () => {
        const iframe = this.$refs.newsIframe;
        if (!iframe) return;
        try {
          const doc = iframe.contentDocument;
          const h = Math.max(
            doc.documentElement.scrollHeight,
            doc.documentElement.offsetHeight,
            doc.body.scrollHeight,
            doc.body.offsetHeight
          );
          if (h > 0) this.htmlIframeHeight = h;
        } catch {
          /* cross-origin — no-op */
        }
      };
      // Primera medición rápida
      setTimeout(measure, 80);
      // Segunda medición: captura fuentes e imágenes que cargan tarde
      setTimeout(measure, 450);
    },

    _onIframeMessage(e) {
      if (e.data === "CLOSE_MODAL") this.closeNews("close");
    },

    docStatusLabel(status) {
      const map = {
        draft: "Borrador",
        owner_signed: "Firmado",
        sent: "Enviado",
        viewed: "Visto",
        partially_signed: "Parcialmente firmado",
        signed: "Completado",
        expired: "Expirado",
        cancelled: "Cancelado",
        downloaded: "Descargado"
      };
      return map[status] || status;
    }
  },

  created() {
    // Prefetch summary before child components mount so they share one HTTP request
    dashboardService.prefetchSummary(this.period);
  },
  mounted() {
    this.getNews();
    window.addEventListener("message", this._onIframeMessage);
    if (this.$store.getters.hasFeature("signing")) {
      this.loadDocSummary();
    }
  },

  beforeDestroy() {
    window.removeEventListener("message", this._onIframeMessage);
  }
};
</script>

<style lang="scss">
/* ── News modal — global (custom-class requires non-scoped) ── */

/*
 * Cuando el modal es de tipo HTML el wrapper debe alinearse desde arriba
 * (no centrado verticalmente) para que el scroll del wrapper sea quien
 * controle el overflow — no el propio modal ni sus hijos.
 * Se scoped vía :has() para no afectar otros dialogs.
 */
.el-dialog__wrapper:has(.news-modal--html) {
  align-items: flex-start !important;
  padding: 16px 0 !important;
}

.el-dialog.news-modal {
  border-radius: 16px;
  overflow: hidden;
  max-width: 760px;
  width: 96% !important;
  margin: auto;
  .el-dialog__header {
    padding: 0;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
  }
  .el-dialog__body {
    padding: 0;
  }
  .el-dialog__footer {
    padding: 0;
  }

  &.news-modal--html {
    background: transparent !important;
    box-shadow: none !important;
    border-radius: 16px !important;
    overflow: visible !important;
    max-height: none !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;

    .el-dialog__header {
      display: none !important;
    }
    .el-dialog__footer {
      display: none !important;
    }
    .el-dialog__body {
      padding: 0 !important;
      background: transparent !important;
      overflow: visible !important;
    }
    .news-modal__body {
      background: transparent !important;
      max-height: none !important;
      overflow: visible !important;
    }
    .news-modal__iframe {
      display: block;
      width: 100%;
      border: 0;
      background: transparent !important;
    }
  }
}
.news-modal__iframe {
  display: block;
  width: 100%;
  height: 0; /* JS sets exact height after load */
  border: none;
  background: transparent;
}

/* Botón X flotante para variante HTML */
.news-modal__close-html {
  position: absolute;
  top: 12px;
  right: 35px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.92);
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 18px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #fff;
    color: #374151;
  }
}
.news-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  gap: 12px;
}
.news-modal__title {
  font-size: 16px;
  font-weight: 700;
  color: #31355c;
  line-height: 1.3;
  flex: 1;
}
.news-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 20px;
  padding: 4px;
  border-radius: 6px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.2s, background 0.2s;
  &:hover {
    color: #374151;
    background: #f3f4f6;
  }
}
.news-modal__body {
  overflow-y: auto;
  max-height: 82vh;
}
.news-modal__media {
  width: 100%;
}
.news-modal__image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}
.news-modal__video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: #000;
  .news-modal__video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
}
.news-modal__text {
  padding: 16px 20px;
  p {
    margin: 0;
    color: #4b5563;
    font-size: 14px;
    line-height: 1.6;
  }
}
.news-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}
.news-modal__btn-close {
  background: none;
  border: 1.5px solid #d1d5db;
  color: #6b7280;
  border-radius: 10px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #9ca3af;
    color: #374151;
    background: #f9fafb;
  }
}
.news-modal__btn-cta {
  display: inline-block;
  background: #3fdbbf;
  color: #fff !important;
  border: none;
  border-radius: 10px;
  padding: 9px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none !important;
  transition: background 0.2s, transform 0.1s;
  &:hover {
    background: #31c4a8;
    transform: translateY(-1px);
  }
}
@media (max-width: 575px) {
  .el-dialog.news-modal {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    border-radius: 0 !important;
  }
  .news-modal__footer {
    flex-direction: column-reverse;
    gap: 8px;
    .news-modal__btn-close,
    .news-modal__btn-cta {
      width: 100%;
      text-align: center;
    }
  }
}
</style>

<style lang="scss" scoped>
.bi-upgrade-card {
  background: #fff;
  border: 1.5px dashed #d0ede8;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  height: 100%;

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: #f0faf7;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0;
  }

  &__desc {
    font-size: 13px;
    color: #888780;
    margin: 0;
    line-height: 1.5;
  }

  &__btn {
    display: inline-block;
    margin-top: 4px;
    padding: 8px 20px;
    background: #2d2b55;
    color: #fff;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.15s;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
      background: #3ecfb4;
      color: #fff;
      text-decoration: none;
    }
  }

  // Versión horizontal (inline, ancho completo)
  &--inline {
    flex-direction: row;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border: 1.5px dashed #d0ede8;

    .bi-upgrade-card__icon {
      width: 36px;
      height: 36px;
      margin-bottom: 0;
      flex-shrink: 0;
    }

    .bi-upgrade-card__text {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .bi-upgrade-card__title {
      font-size: 14px;
      margin: 0;
    }

    .bi-upgrade-card__desc {
      font-size: 12px;
    }

    .bi-upgrade-card__btn {
      margin-top: 0;
    }
  }
}

.header-right-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.privacy-toggle {
  width: 34px;
  height: 34px;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  background: #fff;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  padding: 0;
  &:hover {
    color: #31355c;
    border-color: #c0c8d8;
  }
  &.active {
    background: #31355c;
    border-color: #31355c;
    color: #fff;
  }
}
.dashboard-bi {
  background: #fff;
  min-height: 100vh;
}
.dashboard-bi-content {
  max-width: 1400px;
}
.bi-card {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  padding: 16px 18px;
  height: 100%;
}
.bi-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.bi-card-title {
  font-size: 16px;
  font-weight: 500;
  color: #31355c;
  margin: 0;
  text-transform: none;
  letter-spacing: normal;
}
.bi-card-count {
  font-size: 10px;
  font-weight: 700;
  background: #e24b4a;
  color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bi-card-info {
  font-size: 14px;
  color: #c0c4cc;
  cursor: help;
  transition: color 0.15s;
  &:hover {
    color: #909399;
  }
}
.bi-card-link {
  margin-left: auto;
  font-size: 13px;
  color: #3fdbbf;
  text-decoration: none;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
}

/* Document summary strip */
.doc-summary-strip {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 10px 0 14px;
  flex-wrap: wrap;
}
.doc-summary-strip__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  min-width: 80px;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.8;
  }
}
.doc-summary-strip__count {
  font-size: 28px;
  font-weight: 700;
  color: #31355c;
  line-height: 1;
  &--green {
    color: #1d9e75;
  }
  &--red {
    color: #e24b4a;
  }
}
.doc-summary-strip__label {
  font-size: 12px;
  color: #989aae;
  white-space: nowrap;
}
.doc-summary-strip__btn {
  background: #3fdbbf;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
  &:hover {
    background: #30c5aa;
  }
}

/* Pending documents list */
.doc-pending-list {
  border-top: 1px solid #e4eaf6;
  padding-top: 12px;
}
.doc-pending-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f7f8fc;
  }
  i {
    color: #989aae;
    font-size: 16px;
  }
}
.doc-pending-item__name {
  flex: 1;
  font-size: 13px;
  color: #31355c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.doc-pending-item__status {
  font-size: 12px;
  color: #989aae;
  white-space: nowrap;
}

/* ── Dashboard skeleton ── */
@keyframes bi-shimmer {
  0% {
    background-position: -600px 0;
  }
  100% {
    background-position: 600px 0;
  }
}
.bi-skel {
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e4e8f1 50%, #f0f2f7 75%);
  background-size: 1200px 100%;
  animation: bi-shimmer 1.4s infinite linear;

  &--banner {
    height: 240px;
    width: 100%;
  }
  &--kpi {
    height: 100px;
    flex: 1;
    min-width: 0;
  }
  &--secondary {
    height: 90px;
    width: 100%;
  }
  &--chart {
    height: 180px;
    width: 100%;
  }
}
.bi-skel-kpis {
  display: flex;
  gap: 12px;
  height: 100%;
  align-items: stretch;
  flex-wrap: wrap;
  .bi-skel--kpi {
    min-height: 100px;
  }
}
</style>
